import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import linkifyMessageContent from '~pages/Messages/linkifyMessageContent';

type MessageContentProps = {
  content: string;
  isDeleted: boolean;
};

export default function MessageContent({ content, isDeleted }: MessageContentProps) {
  const theme = useTheme();
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const openLink = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
    setPendingHref(null);
  };

  return (
    <>
      <Typography
        data-test-id="messaging-message-body"
        sx={{
          color: isDeleted ? theme.semantic.textSecondary : undefined,
          fontStyle: isDeleted ? 'italic' : undefined,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
        variant="body2"
      >
        {isDeleted
          ? t('messaging.deleted_message')
          : linkifyMessageContent(content, setPendingHref)}
      </Typography>

      {pendingHref !== null && (
        <ConfirmationDialog
          confirmButtonProps={{
            children: t('messaging.external_link_confirm'),
            color: 'primary',
          }}
          isOpen
          onClose={() => setPendingHref(null)}
          onConfirm={() => openLink(pendingHref)}
          title={t('messaging.external_link_title')}
        >
          <Typography variant="body2">{t('messaging.external_link_warning')}</Typography>

          <Box
            sx={{
              backgroundColor: theme.semantic.surfaceSunken,
              border: `1px solid ${theme.semantic.border}`,
              borderRadius: theme.brandV2.borderRadius,
              mt: 1.5,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography
              data-test-id="messaging-external-link-url"
              sx={{ wordBreak: 'break-all' }}
              variant="body2"
            >
              {pendingHref}
            </Typography>
          </Box>
        </ConfirmationDialog>
      )}
    </>
  );
}
